import { TASKS_MODULE_NAME } from '../constants/task'
import { enumNarrow } from '../helpers/enumGuard'
import { ArcVoiceAccent, ArcVoiceCutInSensitivity, ArcVoiceName } from './arcVoice'
import { DEFAULT_CHAT_MODEL, Models } from './atlasai'
import { AuthProvider } from './auth'
import type { ClientInfoStatus } from './client'
import { IParticipant } from './participant'
import { ThreadsFilter } from './threadsFilter'

export enum NotificationSettingType {
  AutoReportingConnected = 'AutoReportingConnected',
  Notes = 'Notes',
  FeesAndAdjustments = 'FeesAndAdjustments',
  Collections = 'Collections',
  PassThrough = 'PassThroughs',
  Funding = 'Funding',
  Activities = 'Activities',
  SubmitReporting = 'SubmitReporting',
  ClientIntake = 'ClientIntake',
  ClientApplication = 'ClientApplication',
  ClientSettings = 'ClientSettings',
  MarketNews = 'MarketNews',
  Tasks = 'Tasks',
  Users = 'Users',
  System = 'System',
  ArcAutomations = 'ArcAutomations',
  /**
   * Arc's email-triage reply drafts. Absent from every existing user's saved
   * settings and deliberately not backfilled — people opt in themselves.
   */
  ArcEmailDrafts = 'ArcEmailDrafts',
  /**
   * Team messaging (channels + DMs). On by default for internal users' banners
   * (backfilled into what is now `desktop` and `mobile`); `email` stays opt-in.
   */
  Messages = 'Messages',
  /**
   * Error Center alerts (new/reopened errors, cron and job failures). Admin-only,
   * absent from every saved setting and not backfilled — the dev team opts in.
   */
  Errors = 'Errors',
}

/** Where a banner lands. Values double as the `IUserNotificationSettings` keys. */
export enum NotificationDeviceType {
  Desktop = 'desktop',
  Mobile = 'mobile',
}

export interface IUserNotificationSettings {
  email: NotificationSettingType[]
  [NotificationDeviceType.Desktop]: NotificationSettingType[]
  [NotificationDeviceType.Mobile]: NotificationSettingType[]
  // When true, suppress collaborator-activity notifications for tasks where the
  // user is only a collaborator/watcher (not the assignee). Orthogonal to the
  // per-channel `Tasks` toggle — stored in the same notification_settings JSONB,
  // so no schema change is needed.
  muteCollaboratorOnly?: boolean
}

export interface IUserDisplaySettings {
  viewRowNumbers: boolean
  /**
   * Silences the audible new-message cue. Server-side rather than localStorage so
   * it follows the user between machines — someone who mutes it on their laptop
   * meant "I don't want this sound", not "not on this device". Optional because
   * every existing row predates it, and absent reads as unmuted.
   */
  muteMessageSound?: boolean
  /** Superseded by `aimThreadsFilter`; still read so an old `true` opens on Unread. */
  aimUnreadOnly?: boolean
  /**
   * Which threads the AIM Threads section lists. Server-side for the same reason
   * `muteMessageSound` is: it's how someone works, not how one laptop behaves.
   */
  aimThreadsFilter?: ThreadsFilter
  /**
   * When this user's three starting My Tasks sections were seeded. Stamped once,
   * on first visit; deleting every section is permanent and nothing re-seeds,
   * which is why presence of sections can't stand in for this flag.
   */
  myTasksSectionsSeededAt?: string
}

export const DEFAULT_USER_DISPLAY_SETTINGS: IUserDisplaySettings = {
  viewRowNumbers: true,
  muteMessageSound: false,
  aimThreadsFilter: ThreadsFilter.Active,
}

/**
 * Per-user Arc behaviour preferences. One JSONB blob rather than a column per
 * setting so a new toggle costs a line here and a line in UpdateArcSettings
 * instead of a migration. Every key is optional and absent reads as the default
 * below — existing rows predate each one.
 *
 * Free-form Arc *content* (aiDossier, aiLearnedProfile, aiSchedulePreference, aiVoiceTone) stays
 * in its own columns: it's long text with its own lifecycle, not a toggle.
 */
export interface IArcSettings {
  /**
   * When false, Arc skips creating a new Gmail draft as part of any operation
   * but completes every other step normally. Does not affect updating an
   * existing draft.
   */
  draftEmailResponses?: boolean
  /**
   * The model a brand-new Arc chat opens on for this user, one of
   * SELECTABLE_MODELS. Absent means "no preference set" — new chats follow
   * DEFAULT_CHAT_MODEL, same as every user before this setting existed. A
   * per-channel arc_model override (channel.arcModel) still wins over this
   * for chats in that channel; this only supplies the fallback when a
   * channel has none set.
   */
  defaultModel?: Models
  /** The voice Arc speaks with on a call. Absent means the deployment's default. */
  voiceName?: ArcVoiceName
  /** The accent Arc speaks with on a call. */
  voiceAccent?: ArcVoiceAccent
  /** How readily talking over Arc on a call cuts it off. */
  voiceCutIn?: ArcVoiceCutInSensitivity
}

export const DEFAULT_ARC_SETTINGS: Required<IArcSettings> = {
  draftEmailResponses: true,
  defaultModel: DEFAULT_CHAT_MODEL,
  voiceName: ArcVoiceName.Eve,
  voiceAccent: ArcVoiceAccent.Natural,
  voiceCutIn: ArcVoiceCutInSensitivity.Normal,
}

/**
 * The flat patch the Arc Settings endpoint accepts — the toggles above plus the
 * free-form Arc content that lives in its own columns. Which storage a key lands
 * in is the service's business, not the caller's. Every key optional: each box on
 * the settings page sends only what it changed.
 */
export interface IArcSettingsPatch extends IArcSettings {
  aiDossier?: string
  aiSchedulePreference?: string
  aiLearnedProfile?: string
  /** Write-only: responses carry `hasFirefliesApiKey` instead. Empty string clears it. */
  firefliesApiKey?: string
}

/** Who wrote a learned-profile change journaled to dream_cycle_action. */
export enum LearnedProfileChangeSource {
  Nightly = 'nightly',
  UserEdit = 'user_edit',
  Revert = 'revert',
}

/** One journaled learned-profile change, as the Arc settings history shows it. */
export interface ILearnedProfileChange {
  id: string
  createdAt: string | Date
  source: LearnedProfileChangeSource
  reason: string
  before: string | null
  after: string | null
}

/**
 * The patch the AIM-profile endpoint accepts. Every key optional: the nickname is
 * edited on the settings tab and the status from the avatar menu, so a request
 * carries one or the other and must not blank whatever it left out.
 *
 * An empty string is meaningful — it is what a cleared input posts, and the
 * service reads it as "unset this". `undefined` means "don't touch".
 */
export interface IAimProfilePatch {
  nickname?: string
  aimStatus?: string
  aimStatusEmoji?: string
  /** ISO string; empty clears the expiry. */
  aimStatusExpiresAt?: string
  /** Opt in/out of calendar-derived statuses. Turning it off clears the derived one. */
  deriveStatusFromCalendar?: boolean
}

/**
 * The only shape of a colleague that may cross to someone outside the firm.
 *
 * An allowlist, not a redaction. `sanitizeUser` below is a denylist, which is the
 * wrong instrument for an external surface twice over: it passes everything
 * nobody thought to name — `aiVoiceTone`, `aiDossier`, `notificationSettings`,
 * `googleEmail`, login timestamps — and it silently passes every column added to
 * the entity after it was written. Naming the six fields a contact card renders
 * means a new column is invisible here until somebody decides otherwise.
 *
 * Used wherever a client, prospect or participant is shown who to contact.
 */
export interface IUserContact {
  firstName: string | null
  lastName: string | null
  email: string
  phone: string | null
  calendlyURL: string | null
  avatar: string | null
}

export interface IUser {
  id: string
  firstName: string
  lastName: string
  email: string
  /**
   * Self-set display name shown in place of `firstName`/`lastName`. Optional
   * because every row predates it, and absent reads as "use the legal name".
   */
  nickname?: string | null
  status: UserStatus
  role: UserRole
  /** A bot account (Arc, Dwight Bot, …), whatever its role. Hidden from user pickers. */
  isBot?: boolean
  /** The companies a client user can switch between (signed-in profile only). */
  clientMemberships?: IClientMembership[]
  phone: string
  calendlyURL: string
  avatar: string
  isInvited: boolean
  isProspectUser: boolean
  // Specifically in diligence, as opposed to isProspectUser which is true for the
  // whole prospect portal. The due-diligence sagas load DD-only data off this —
  // firing those for a pre-underwriting prospect 404s.
  isDueDiligenceUser: boolean
  isAdminLoggedAsClient: boolean
  isNotified: boolean
  isPrimary: boolean
  isViewer: boolean
  isAuthorizedSignature: boolean
  isReportingReminderReceiver: boolean
  clientInfoUsers?: {
    clientInfo?: { clientName?: string; id?: string; clientStatus?: ClientInfoStatus }
  }[]
  salesforceId?: string
  password?: string
  confirmPassword?: string
  notificationSettings: IUserNotificationSettings
  displaySettings?: IUserDisplaySettings
  /**
   * Flat rather than a nested object because these arrive straight off the
   * entity through `sanitizeUser`. Read them through `resolveAimStatus` so an
   * expired status reads as absent — the raw `aimStatus` here can be a status
   * whose `aimStatusExpiresAt` has already passed.
   */
  aimStatus?: string | null
  aimStatusEmoji?: string | null
  aimStatusExpiresAt?: string | Date | null
  /**
   * Derived from the user's calendar by the SyncCalendarStatuses cron. Read
   * through `effectiveAimStatus`, which prefers the manual fields above.
   */
  aimCalendarStatus?: string | null
  aimCalendarStatusEmoji?: string | null
  aimCalendarStatusExpiresAt?: string | Date | null
  /** When false, the calendar sweep never writes a derived status for them. */
  deriveStatusFromCalendar?: boolean
  aiDossier?: string | null
  aiLearnedProfile?: string | null
  aiLearnedProfileUpdatedAt?: string | Date | null
  aiSchedulePreference?: string | null
  arcSettings?: IArcSettings
  hasFirefliesApiKey?: boolean
  aiVoiceTone?: string | null
  aiVoiceToneGeneratedAt?: string | Date | null
  clientName?: string
  clientId?: string
  clientIconUrl?: string
  participantInfoUser: IParticipant

  // Google Authentication fields
  googleId?: string | null
  googleEmail?: string | null
  googleAvatarUrl?: string | null

  microsoftId?: string | null
  microsoftEmail?: string | null

  // Two-Factor Authentication fields
  twoFactorEnabled?: boolean

  authMethod: AuthProvider[]
}

export enum UserStatus {
  New = 'NEW',
  Active = 'ACTIVE',
}

/** Max characters accepted in a nickname and in a status text. */
export const USER_NICKNAME_MAX_LENGTH = 60
export const AIM_STATUS_MAX_LENGTH = 100
/**
 * Emoji are multi-codepoint (a single flag or skin-toned face runs well past
 * four chars), so the column is sized for one grapheme cluster rather than one
 * character.
 */
export const AIM_STATUS_EMOJI_MAX_LENGTH = 16

/**
 * A user's personal status once expiry has been applied — what every consumer
 * should hold, as opposed to the raw columns.
 *
 * Null (rather than an object with null fields) means "no status", so a caller
 * can branch on the object itself.
 */
export interface IAimStatus {
  text: string | null
  emoji: string | null
  /** ISO string. Null means the status has no expiry and stands until changed. */
  expiresAt: string | null
}

/**
 * One person's identity as every non-AIM surface needs it: what to call them and
 * what they're up to.
 *
 * `name` is pre-resolved on the server so a consumer rendering only a name never
 * reimplements the nickname-then-legal-name precedence; `nickname` is kept
 * alongside it for the settings form, which has to show the raw field.
 */
export interface IAimProfileState {
  userId: string
  nickname: string | null
  name: string
  aimStatus: IAimStatus | null
}

/**
 * A user's status as everyone else should see it: what they set, else what their
 * calendar implies, else nothing.
 *
 * Manual always outranks derived. Someone who typed "On vacation" means it more
 * than a 30-minute block on their calendar does, and the two live in separate
 * columns precisely so a sweep can't overwrite the typed one.
 *
 * Both halves go through `liveAimStatus`, so an ended meeting stops showing even
 * if no sweep has run since, and an expired manual status correctly falls THROUGH
 * to the calendar rather than blanking it.
 */
export const effectiveAimStatus = (
  user: Pick<
    IUser,
    | 'aimStatus'
    | 'aimStatusEmoji'
    | 'aimStatusExpiresAt'
    | 'aimCalendarStatus'
    | 'aimCalendarStatusEmoji'
    | 'aimCalendarStatusExpiresAt'
  >,
  now: Date = new Date(),
): IAimStatus | null =>
  resolveAimStatus(user, now) ??
  resolveAimStatus(
    {
      aimStatus: user.aimCalendarStatus,
      aimStatusEmoji: user.aimCalendarStatusEmoji,
      aimStatusExpiresAt: user.aimCalendarStatusExpiresAt,
    },
    now,
  )

/**
 * The LIVR rule for an AIM status expiry.
 *
 * Named, rather than written inline in the service, because the obvious spelling
 * is wrong in a way nothing catches: bare `['iso_date']` defaults to
 * `format: 'date'` and rewrites its own output to `YYYY-MM-DD`, and Base hands
 * `execute()` that validated output. A 30-minute expiry became midnight UTC —
 * already past — so every timed status was invisible the moment it saved, with
 * no type error and no lint warning. Keeping the rule next to `liveAimStatus`
 * keeps the antidote beside the thing it protects, and lets a test assert it
 * without importing the service (and with it, the entity and config tree).
 */
export const AIM_STATUS_EXPIRES_AT_RULE = [{ iso_date: { format: 'datetime' } }]

/**
 * The raw columns → the status shape, with NO expiry applied.
 *
 * Split out because the two callers want different things from the clock: the
 * server resolves against "now" as it serializes, while a client selector must
 * hand back the raw value — a reselect selector memoizes on an unchanged input
 * object, so an expiry applied inside one is computed once and never revisited.
 */
export const toAimStatus = (
  user: Pick<IUser, 'aimStatus' | 'aimStatusEmoji' | 'aimStatusExpiresAt'>,
): IAimStatus | null => {
  const text = user.aimStatus?.trim() || null
  const emoji = user.aimStatusEmoji?.trim() || null
  if (!text && !emoji) return null

  const expiresAt = user.aimStatusExpiresAt ? new Date(user.aimStatusExpiresAt) : null
  return {
    text,
    emoji,
    expiresAt: expiresAt && !Number.isNaN(expiresAt.getTime()) ? expiresAt.toISOString() : null,
  }
}

/**
 * A status → null once its expiry has passed.
 *
 * Takes an already-shaped status, so it works on what a payload or websocket
 * push carried: that value was live when the server sent it, and a tab left open
 * outlives that. Callers re-apply this on a timer (see `useAimStatusTick`)
 * because nothing pushes an expiry — elapsing is a fact about the clock, not an
 * event anyone emits.
 *
 * An unparseable date reads as "no expiry": losing a status is worse than
 * showing one slightly too long.
 */
export const liveAimStatus = (
  status: IAimStatus | null | undefined,
  now: Date = new Date(),
): IAimStatus | null => {
  if (!status) return null
  if (!status.expiresAt) return status

  const expiresAt = new Date(status.expiresAt)
  if (Number.isNaN(expiresAt.getTime())) return status

  return expiresAt.getTime() <= now.getTime() ? null : status
}

/**
 * The raw columns → a status with expiry applied, or null when there is nothing
 * (or nothing still valid) to show. What the server builds payloads from.
 */
export const resolveAimStatus = (
  user: Pick<IUser, 'aimStatus' | 'aimStatusEmoji' | 'aimStatusExpiresAt'>,
  now: Date = new Date(),
): IAimStatus | null => liveAimStatus(toAimStatus(user), now)

/**
 * A portal user's sign-in link, handed back instead of emailed so whoever owns
 * the relationship can send it themselves (see server users/InviteLink).
 */
export interface IUserInviteLink {
  /** Password-setup URL, or the sign-in page when the account already has one. */
  link: string
  /** True when the user could already sign in, so no setup link was minted. */
  isExistingAccount: boolean
}

export interface IClientMembership {
  clientId: string
  clientName: string
  isPrimary: boolean
}

export const USER_STATUS_LABEL: Record<UserStatus, string> = {
  [UserStatus.New]: 'New',
  [UserStatus.Active]: 'Active',
}

export enum UserRole {
  ADMIN = 'ADMIN',
  PORTFOLIO_ADMIN = 'PORTFOLIO_ADMIN',
  PORTFOLIO_USER = 'PORTFOLIO_USER',
  CLIENT_USER = 'CLIENT_USER',
  PARTICIPANT_USER = 'PARTICIPANT_USER',
  BDO = 'BUSINESS_DEVELOPMENT_USER',
  UW_USER = 'UW_USER',
  BOT_USER = 'BOT_USER',
  /**
   * Internal, but Tasks and AIM and nothing else: two nav items, their own
   * account settings, no loan data. Deliberately outside `INTERNAL`, which is the
   * gate on virtually every other service in the app, so this role is denied
   * everywhere by default and reaches a surface only where it was named
   * explicitly (see `MESSAGING` and `TASKS`).
   *
   * Arc is reachable inside those two surfaces, with its tool set narrowed to
   * match — see server `atlasai/tools/roleToolAccess`.
   */
  TASKS_AND_MESSAGING = 'TASKS_AND_MESSAGING',
}

export enum AdminUserNames {
  BD_ADMIN = 'Ben Brachot',
}

export const USER_ROLE_LABEL = {
  [UserRole.ADMIN]: 'System Admin',
  [UserRole.PORTFOLIO_ADMIN]: 'Relationship Manager',
  [UserRole.PORTFOLIO_USER]: 'Ops User',
  [UserRole.CLIENT_USER]: 'Client',
  [UserRole.BDO]: 'Business Development User',
  [UserRole.UW_USER]: 'Underwriter User',
  [UserRole.PARTICIPANT_USER]: 'Participant User',
  [UserRole.BOT_USER]: 'Bot',
  [UserRole.TASKS_AND_MESSAGING]: 'Tasks & Messages',
}

export const USER_ROLE_LABEL_SHORT: Partial<Record<UserRole, string>> = {
  [UserRole.ADMIN]: 'Admin',
  [UserRole.PORTFOLIO_ADMIN]: 'RM',
  [UserRole.PORTFOLIO_USER]: 'Ops',
  [UserRole.CLIENT_USER]: 'Client',
  [UserRole.BDO]: 'BDO',
  [UserRole.UW_USER]: 'UW',
  [UserRole.BOT_USER]: 'Bot',
  [UserRole.TASKS_AND_MESSAGING]: 'Tasks+Msg',
}

/**
 * The two bases every "X, plus something" group below is derived from, rather
 * than re-listing its members: portfolio staff, and all internal staff (the same
 * four plus BDO). A role added to either reaches every group built on it; a
 * copy-pasted list is how one silently misses it.
 */
const PORTFOLIO_ROLES = [
  UserRole.PORTFOLIO_ADMIN,
  UserRole.PORTFOLIO_USER,
  UserRole.ADMIN,
  UserRole.UW_USER,
]

const INTERNAL_ROLES = [...PORTFOLIO_ROLES, UserRole.BDO]

export const userRoleGroups = {
  ADMINS: [UserRole.ADMIN],
  UW_USERS: [UserRole.UW_USER, UserRole.ADMIN],
  PORTFOLIO: [...PORTFOLIO_ROLES],
  /**
   * Who may reach the diligence module: portfolio staff, plus BD.
   *
   * Two different grants share this group. A *deal* is read-only for BD — its
   * list, stats and record — and every service that mutates one (Update, RunBBC,
   * UploadDocuments, ProcessDocuments, the document requests) stays on
   * `PORTFOLIO`. Diligence *settings* — default notes, default document-request
   * types, default LCR commentary — is fully BD's, reads and writes, because it
   * is shared configuration rather than anyone's loan.
   *
   * A separate name rather than adding BDO to `PORTFOLIO`, which gates loan
   * servicing and reporting too. Same membership as `INTERNAL` today; the name is
   * what says which surface it grants, so widening one doesn't silently widen the
   * other. `bdModuleAccess.test.ts` pins the split.
   */
  DILIGENCE_VIEWERS: [...INTERNAL_ROLES],
  PORTFOLIO_AND_CLIENT: [...PORTFOLIO_ROLES, UserRole.CLIENT_USER],
  // Managing portal users. BDOs are included so they can invite and revoke
  // prospect logins from the prospect page, but every service using this group
  // must also call assertCanManageClientUsers, which confines a BDO to
  // prospect-stage records.
  PORTFOLIO_AND_CLIENT_AND_BDO: [...INTERNAL_ROLES, UserRole.CLIENT_USER],
  PORTFOLIO_ADMIN: [UserRole.PORTFOLIO_ADMIN, UserRole.ADMIN, UserRole.PORTFOLIO_USER],
  PORTFOLIO_AND_PARTICIPANT: [...PORTFOLIO_ROLES, UserRole.PARTICIPANT_USER],
  CLIENTS: [UserRole.CLIENT_USER],
  BDOS: [UserRole.BDO, UserRole.UW_USER, UserRole.ADMIN],
  // Who may permanently delete a never-funded prospect: narrower than BDOS, which includes UW.
  PROSPECT_DELETERS: [UserRole.BDO, UserRole.ADMIN],
  INTERNAL: [...INTERNAL_ROLES],
  // Valid assignees for a task: every internal role, plus Arc (BOT_USER) so a
  // task can be assigned to / tagged for the bot. Single source of truth for
  // the task roster (My Tasks, Task Projects, the task detail drawer), which
  // loads it with `includeBots`, so every bot account stays assignable.
  TASK_ASSIGNEES: [
    ...INTERNAL_ROLES,
    UserRole.BOT_USER,
    // Tasks are half of what this role has, so work can be assigned to them the
    // same way it is to any teammate — and they can assign it to Arc the same way.
    UserRole.TASKS_AND_MESSAGING,
  ],
  // Who can be a participant in a conversation: every internal role, plus Arc
  // (BOT_USER) so it can be messaged directly the way any teammate can. Same shape
  // and same reason as TASK_ASSIGNEES above — Arc is a member of the team, not a
  // feature of the channel it happens to be tagged in. Keep `OpenDm` and
  // `AddMembers` pointed here so BOT_USER can't silently drop out again.
  //
  // `AddMembers` used INTERNAL, which excludes BOT_USER, so every attempt to add
  // Arc to an existing conversation filtered down to nobody and failed with "None
  // of those users can be added". Being mentioned is how Arc joins a *channel*; a
  // conversation you want Arc in from the start has nothing to mention it in yet.
  // AIM is half of what the tasks-and-messaging role has, so leaving it out would
  // make that half impossible: nobody could open a conversation with them, and
  // `resolveDmChannel` would reject them as "cannot be messaged".
  DM_PARTICIPANTS: [...INTERNAL_ROLES, UserRole.BOT_USER, UserRole.TASKS_AND_MESSAGING],
  /**
   * Who can use team messaging (AIM): every internal role plus the
   * tasks-and-messaging role. The gate on the channel services and on the
   * mention picker that feeds them.
   *
   * Deliberately not `INTERNAL` itself — that group gates ~400 services across
   * the whole app, so widening it is how a two-surface user quietly gets
   * underwriting.
   */
  MESSAGING: [...INTERNAL_ROLES, UserRole.TASKS_AND_MESSAGING],
  /**
   * Who can use Tasks: the same membership as `MESSAGING`, and a separate name
   * on purpose. These are two independently granted product surfaces — the gate
   * each service names says which one it belongs to, and each has its own drift
   * guard in tasksAndMessagingRole.test.ts. Covers the tasks, taskProjects,
   * customFields and projectCustomFields services.
   *
   * Services that also serve Arc spell that out as `[...userRoleGroups.TASKS,
   * UserRole.BOT_USER]`, the way they previously extended `INTERNAL`.
   */
  TASKS: [...INTERNAL_ROLES, UserRole.TASKS_AND_MESSAGING],
  DD: [UserRole.ADMIN, UserRole.UW_USER, UserRole.CLIENT_USER],
  // Every human role. Doubles as the allowlist for what an admin may assign on
  // the Users page, which is why the tasks-and-messaging role belongs here — the
  // services gated on it act on the caller's own account (avatar, push
  // subscriptions, feedback, linked sign-in providers), not on portfolio data.
  ALL: [
    ...INTERNAL_ROLES,
    UserRole.CLIENT_USER,
    UserRole.PARTICIPANT_USER,
    UserRole.TASKS_AND_MESSAGING,
  ],
  /**
   * Who may read a client's account activity and customers. Exactly what `ALL`
   * meant before the tasks-and-messaging role joined it: these endpoints take a
   * clientId and hand back that client's ledger, which is loan data, so a role
   * that has only Tasks and AIM must not inherit them just by being a signed-in
   * human.
   */
  CLIENT_ACTIVITY_VIEWERS: [...INTERNAL_ROLES, UserRole.CLIENT_USER, UserRole.PARTICIPANT_USER],
  INTERNAL_AND_CLIENTS: [...INTERNAL_ROLES, UserRole.CLIENT_USER],
  INTERNAL_AND_PARTICIPANTS: [...INTERNAL_ROLES, UserRole.PARTICIPANT_USER],
  /**
   * Who may read a diligence record's team lists: everyone internal, plus the
   * client themselves and participants.
   *
   * Was `ALL_EXCEPT_BDO`, whose entire purpose was to keep BD out. BD now reads
   * the whole diligence surface, so the exclusion — and the name built around it
   * — no longer describe anything true.
   */
  DILIGENCE_RECORD_VIEWERS: [...INTERNAL_ROLES, UserRole.CLIENT_USER, UserRole.PARTICIPANT_USER],
  BOTS: [UserRole.BOT_USER],
  /**
   * Who may create, list and revoke their own personal access tokens: every internal user,
   * developers on the tasks-and-messaging role included. A token's scopes narrow it further.
   */
  ACCESS_TOKEN_HOLDERS: [...INTERNAL_ROLES, UserRole.TASKS_AND_MESSAGING],
  /** Who can see and act on the Error Center. Named so a future dev role is a one-line add. */
  ERROR_CENTER: [UserRole.ADMIN],
  EXTERNAL_API: [] as UserRole[],
}

export const NOTIFICATION_SETTING_LABEL: Record<NotificationSettingType, string> = {
  [NotificationSettingType.AutoReportingConnected]: 'Auto reporting',
  [NotificationSettingType.Notes]: 'Notes & replies',
  [NotificationSettingType.FeesAndAdjustments]: 'Fees, adjustments & balance alerts',
  [NotificationSettingType.Collections]: 'Collections',
  [NotificationSettingType.PassThrough]: 'Pass-throughs',
  [NotificationSettingType.Funding]: 'Fundings & BBCs',
  [NotificationSettingType.Activities]: 'Activities',
  [NotificationSettingType.SubmitReporting]: 'Ongoing reporting',
  [NotificationSettingType.ClientIntake]: 'Client intake',
  [NotificationSettingType.ClientApplication]: 'Client application',
  [NotificationSettingType.ClientSettings]: 'Loan terms & amendments',
  [NotificationSettingType.MarketNews]: 'Market news',
  [NotificationSettingType.Tasks]: TASKS_MODULE_NAME,
  [NotificationSettingType.Users]: 'User accounts',
  [NotificationSettingType.System]: 'System',
  [NotificationSettingType.ArcAutomations]: 'Arc automations & reminders',
  [NotificationSettingType.ArcEmailDrafts]: 'Arc email drafts',
  [NotificationSettingType.Messages]: 'AIM',
  [NotificationSettingType.Errors]: 'System errors',
}

export interface INotificationSettingRow {
  type: NotificationSettingType
  /** What the row covers, shown behind its info icon. */
  description: string
}

export interface INotificationSettingSection {
  title: string
  rows: INotificationSettingRow[]
}

/** The Notification Settings page layout: section order, then row order within each. */
export const NOTIFICATION_SETTING_SECTIONS: INotificationSettingSection[] = [
  {
    title: 'Collaboration',
    rows: [
      {
        type: NotificationSettingType.Messages,
        description: 'Channel messages and @mentions in AIM.',
      },
      {
        type: NotificationSettingType.Tasks,
        description:
          'Tasks assigned to you, due, or auto-assigned, @mentions on tasks, and activity on tasks you collaborate on.',
      },
      {
        type: NotificationSettingType.Notes,
        description:
          'Note tags and replies (including diligence and LCR notes), diligence call answers, call major developments that need matching, and OPS write-up comments.',
      },
    ],
  },
  {
    title: 'Arc',
    rows: [
      {
        type: NotificationSettingType.ArcAutomations,
        description:
          'Arc automation runs finishing, Arc watches firing, Arc reminders, and Arc chats shared with you.',
      },
      {
        type: NotificationSettingType.ArcEmailDrafts,
        description: 'Arc prepared a draft reply to one of your emails.',
      },
    ],
  },
  {
    title: 'Client accounts',
    rows: [
      {
        type: NotificationSettingType.Funding,
        description:
          'Funding requests, cancellations and deletions, BBCs signed, and BBC processing problems.',
      },
      {
        type: NotificationSettingType.Collections,
        description: 'Checks and wires added, updated or deleted.',
      },
      {
        type: NotificationSettingType.PassThrough,
        description: 'Pass-throughs updated or deleted.',
      },
      {
        type: NotificationSettingType.FeesAndAdjustments,
        description:
          'Fees and adjustments added, changed, removed or charged, loan balance mismatches, term loan activity and terminations, and scheduled items.',
      },
      {
        type: NotificationSettingType.ClientSettings,
        description:
          'Amendments (including applied and voided), overadvances, term loans, maturity and notice-period reminders, and authorized signers added.',
      },
      {
        type: NotificationSettingType.SubmitReporting,
        description:
          'Client reporting submitted, accounting syncs for portfolio clients, and reporting emails Arc filed.',
      },
      {
        type: NotificationSettingType.Activities,
        description:
          'Activities assigned or deleted, check posting issues, and check upload reminders.',
      },
      {
        type: NotificationSettingType.AutoReportingConnected,
        description:
          'Accounting and bank connections (QuickBooks, Plaid, auto reporting) connected, disconnected or expired, sync failures, and client-run manual syncs.',
      },
    ],
  },
  {
    title: 'Deals',
    rows: [
      {
        type: NotificationSettingType.ClientIntake,
        description:
          'Intake forms submitted, prospects reactivated, OPS submitted, in review, approved or processed, BD accounting syncs, and intake documents and lead referrals Arc filed from email.',
      },
      {
        type: NotificationSettingType.ClientApplication,
        description:
          'Application files uploaded, steps finished, document comments, due diligence document reminders, and UW accounting syncs.',
      },
      {
        type: NotificationSettingType.MarketNews,
        description: 'Market news items and digests.',
      },
    ],
  },
  {
    title: 'Admin',
    rows: [
      {
        type: NotificationSettingType.Users,
        description: 'Users created or deleted.',
      },
      {
        type: NotificationSettingType.Errors,
        description: 'Error Center alerts: new or reopened errors, and cron and job failures.',
      },
    ],
  },
]

export const USER_ROLE_NOTIFICATION_SETTINGS: Partial<Record<UserRole, NotificationSettingType[]>> =
  {
    [UserRole.ADMIN]: [
      NotificationSettingType.AutoReportingConnected,
      NotificationSettingType.Notes,
      NotificationSettingType.FeesAndAdjustments,
      NotificationSettingType.ClientSettings,
      NotificationSettingType.Collections,
      NotificationSettingType.PassThrough,
      NotificationSettingType.Funding,
      NotificationSettingType.Activities,
      NotificationSettingType.SubmitReporting,
      NotificationSettingType.ClientIntake,
      NotificationSettingType.ClientApplication,
      NotificationSettingType.MarketNews,
      NotificationSettingType.Tasks,
      NotificationSettingType.Users,
      NotificationSettingType.ArcAutomations,
      NotificationSettingType.ArcEmailDrafts,
      NotificationSettingType.Messages,
      NotificationSettingType.Errors,
    ],
    [UserRole.PORTFOLIO_ADMIN]: [
      NotificationSettingType.AutoReportingConnected,
      NotificationSettingType.Notes,
      NotificationSettingType.FeesAndAdjustments,
      NotificationSettingType.ClientSettings,
      NotificationSettingType.Collections,
      NotificationSettingType.PassThrough,
      NotificationSettingType.Funding,
      NotificationSettingType.Activities,
      NotificationSettingType.SubmitReporting,
      NotificationSettingType.ClientIntake,
      NotificationSettingType.ClientApplication,
      NotificationSettingType.Tasks,
      NotificationSettingType.Users,
      NotificationSettingType.ArcAutomations,
      NotificationSettingType.ArcEmailDrafts,
      NotificationSettingType.Messages,
    ],
    [UserRole.PORTFOLIO_USER]: [
      NotificationSettingType.AutoReportingConnected,
      NotificationSettingType.Notes,
      NotificationSettingType.FeesAndAdjustments,
      NotificationSettingType.ClientSettings,
      NotificationSettingType.Collections,
      NotificationSettingType.PassThrough,
      NotificationSettingType.Funding,
      NotificationSettingType.Activities,
      NotificationSettingType.SubmitReporting,
      NotificationSettingType.ClientIntake,
      NotificationSettingType.ClientApplication,
      NotificationSettingType.Tasks,
      NotificationSettingType.Users,
      NotificationSettingType.ArcAutomations,
      NotificationSettingType.ArcEmailDrafts,
      NotificationSettingType.Messages,
    ],
    [UserRole.BDO]: [
      NotificationSettingType.AutoReportingConnected,
      NotificationSettingType.Notes,
      NotificationSettingType.ClientIntake,
      NotificationSettingType.ClientApplication,
      NotificationSettingType.MarketNews,
      NotificationSettingType.Tasks,
      NotificationSettingType.Users,
      NotificationSettingType.ArcAutomations,
      NotificationSettingType.ArcEmailDrafts,
      NotificationSettingType.Messages,
    ],
    // UW is in the PORTFOLIO recipient group, so it receives the portfolio categories too.
    [UserRole.UW_USER]: [
      NotificationSettingType.AutoReportingConnected,
      NotificationSettingType.Notes,
      NotificationSettingType.FeesAndAdjustments,
      NotificationSettingType.ClientSettings,
      NotificationSettingType.Collections,
      NotificationSettingType.PassThrough,
      NotificationSettingType.Funding,
      NotificationSettingType.Activities,
      NotificationSettingType.SubmitReporting,
      NotificationSettingType.ClientIntake,
      NotificationSettingType.ClientApplication,
      NotificationSettingType.Tasks,
      NotificationSettingType.Users,
      NotificationSettingType.ArcAutomations,
      NotificationSettingType.ArcEmailDrafts,
      NotificationSettingType.Messages,
    ],
    [UserRole.CLIENT_USER]: [
      NotificationSettingType.Notes,
      NotificationSettingType.ClientApplication,
    ],
    [UserRole.BOT_USER]: [],
    // Their two surfaces, and the only two things there is anything to notify
    // them about. `allowedNotificationTypesForRole` (common/interfaces/notification)
    // is what enforces the same scope on the feed itself.
    [UserRole.TASKS_AND_MESSAGING]: [
      NotificationSettingType.Messages,
      NotificationSettingType.Tasks,
    ],
  }

/**
 * Categories a role may turn on but never gets by default — seeded settings for a
 * new user skip them. Errors pings on every failure alert; the dev team opts in.
 */
export const OPT_IN_ONLY_NOTIFICATION_SETTINGS: NotificationSettingType[] = [
  NotificationSettingType.Errors,
]

/**
 * Categories a role receives through a broad recipient group rather than as its own work,
 * so a new user starts with them on desktop only (an in-app notice, no email or phone push).
 */
const DESKTOP_ONLY_DEFAULT_NOTIFICATION_SETTINGS: Partial<
  Record<UserRole, NotificationSettingType[]>
> = {
  [UserRole.PORTFOLIO_ADMIN]: [NotificationSettingType.ClientApplication],
  [UserRole.PORTFOLIO_USER]: [NotificationSettingType.ClientApplication],
  [UserRole.BDO]: [NotificationSettingType.ClientApplication],
  [UserRole.UW_USER]: [
    NotificationSettingType.FeesAndAdjustments,
    NotificationSettingType.ClientSettings,
    NotificationSettingType.Collections,
    NotificationSettingType.PassThrough,
    NotificationSettingType.Funding,
    NotificationSettingType.Activities,
    NotificationSettingType.SubmitReporting,
  ],
  [UserRole.CLIENT_USER]: [NotificationSettingType.Notes],
}

/** A new user's starting email, desktop and mobile categories for their role. */
export const defaultNotificationSettingsForRole = (role: UserRole): IUserNotificationSettings => {
  const desktop = (USER_ROLE_NOTIFICATION_SETTINGS[role] || []).filter(
    (setting) => !OPT_IN_ONLY_NOTIFICATION_SETTINGS.includes(setting),
  )
  const desktopOnly = DESKTOP_ONLY_DEFAULT_NOTIFICATION_SETTINGS[role] || []
  const everyChannel = desktop.filter((setting) => !desktopOnly.includes(setting))
  return { email: everyChannel, desktop, mobile: everyChannel }
}

/** The page's sections cut down to the rows a role is offered; sections left empty are dropped. */
export const notificationSettingSectionsForRole = (
  role: UserRole,
): INotificationSettingSection[] => {
  const offered = USER_ROLE_NOTIFICATION_SETTINGS[role] || []
  return NOTIFICATION_SETTING_SECTIONS.map((section) => ({
    ...section,
    rows: section.rows.filter(({ type }) => offered.includes(type)),
  })).filter((section) => section.rows.length > 0)
}

/** Cast-free narrower for AdminUserNames (see common/helpers/enumGuard). */
export const asAdminUserName = enumNarrow(AdminUserNames).as
